import { useCallback, useEffect, useState } from "react";
import type {
  ChatBroadcastMessage,
  ChatItem,
  ChatReplyRef,
  PollState,
  RoomUser,
  VoteState,
} from "@yay/shared";
import { SHOWCASE_ROOM, SHOWCASE_ROOM_LEGACY } from "@yay/shared";

export { SHOWCASE_ROOM };

const T0 = 1_700_000_000_000;

export function formatPollClosedText(poll: PollState): string {
  const rows = poll.options.map((label, i) => ({
    label,
    n: poll.votes[i]?.length ?? 0,
  }));
  const breakdown = rows.map((r) => `${r.label} ${r.n}`).join(" · ");
  const top = Math.max(0, ...rows.map((r) => r.n));
  if (top === 0) return `poll closed: ${poll.title} — no votes`;
  const winners = rows.filter((r) => r.n === top);
  if (winners.length > 1) {
    return `poll closed: ${poll.title} — tie (${breakdown})`;
  }
  return `poll closed: ${poll.title} — ${winners[0]!.label} won (${breakdown})`;
}

export function isShowcaseRoom(name: string): boolean {
  return name === SHOWCASE_ROOM || name === SHOWCASE_ROOM_LEGACY;
}

export function isShowcaseId(id: string | undefined): boolean {
  return !!id && id.startsWith("sc-");
}

/** Fixture chat + overlays so `?room=dev-showcase` shows every chat UI state. */
export function showcaseFixtures(
  meUsername: string,
  meId?: string,
): {
  messages: ChatItem[];
  poll: PollState;
  vote: VoteState;
  users: RoomUser[];
} {
  const me = meUsername || "you";
  const myId = meId || "u-you";
  let t = T0;
  const next = () => (t += 4_000);

  const messages: ChatItem[] = [
    { type: "system", text: "theo joined the room", timestamp: next() },
    {
      type: "system",
      kind: "room_password",
      text: "Room password set — hunter2",
      timestamp: next(),
    },
    { type: "system", text: "noor is now watching", timestamp: next() },
    {
      type: "chat",
      id: "sc-chat-1",
      username: "theo",
      color: "#f5a623",
      text: "can we get more lo-fi in the queue",
      timestamp: next(),
    },
    {
      type: "chat",
      id: "sc-chat-2",
      username: "noor",
      color: "#a970ff",
      text: `hey @${me} the stickers are actually good`,
      timestamp: next(),
      reactions: { "🔥": ["u-theo", "u-ria"], "😂": ["u-leo"] },
    },
    {
      type: "chat",
      id: "sc-chat-3",
      username: "ria",
      color: "#ff5d5d",
      text: "this song goes hard",
      timestamp: next(),
    },
    {
      type: "whisper",
      id: "sc-wh-1",
      username: "maya",
      color: "#7ee2a8",
      targetName: "ria",
      text: "psst — check the queue",
      timestamp: next(),
    },
    {
      type: "whisper",
      id: "sc-wh-2",
      username: "leo",
      color: "#45b7d1",
      targetName: me,
      text: "this one is for you",
      timestamp: next(),
    },
    {
      type: "moderation",
      action: "kick",
      actorName: "maya",
      targetNames: ["ria"],
      timestamp: next(),
    },
    {
      type: "moderation",
      action: "ban",
      actorName: "jules",
      targetNames: ["ivy"],
      timestamp: next(),
    },
    {
      type: "moderation",
      action: "mute",
      actorName: "maya",
      targetNames: ["theo"],
      timestamp: next(),
    },
    {
      type: "moderation",
      action: "unmute",
      actorName: "maya",
      targetNames: ["theo"],
      timestamp: next(),
    },
    {
      type: "moderation",
      action: "unban",
      actorName: "jules",
      targetNames: ["ivy"],
      timestamp: next(),
    },
    {
      type: "moderation",
      action: "transfer",
      actorName: "maya",
      targetNames: ["noor"],
      timestamp: next(),
    },
    { type: "system", text: "noor muted the room", timestamp: next(), kind: "flash_lock" },
    { type: "system", text: "leo left the room", timestamp: next() },
    {
      type: "system",
      kind: "vote",
      text: "carol was kicked by room vote",
      timestamp: next(),
    },
    {
      type: "system",
      kind: "vote",
      text: "vote to ban ivy failed (1–3)",
      timestamp: next(),
    },
    {
      type: "system",
      text: "Chat is frozen by an operator",
      timestamp: next(),
    },
    {
      type: "system",
      text: "Chat was cleared by an operator",
      timestamp: next(),
    },
    {
      type: "system",
      kind: "admin",
      text: "keep it kind — this is the lobby",
      timestamp: next(),
    },
    {
      type: "system",
      text: "theo was kicked by an operator",
      timestamp: next(),
    },
    {
      type: "system",
      text: "Room was reset by an operator",
      timestamp: next(),
    },
    {
      type: "chat",
      id: "sc-chat-4",
      username: "leo",
      color: "#45b7d1",
      text: "ok we're back",
      timestamp: next(),
    },
  ];

  const poll: PollState = {
    id: "sc-poll",
    title: "What next?",
    options: ["more lo-fi", "switch to live", "nerd mode"],
    votes: [
      ["u-theo", "u-ria", myId],
      ["u-noor"],
      ["u-leo", "u-maya"],
    ],
    closed: false,
    createdBy: myId,
    createdByName: "noor",
    expiresAt: Date.now() + 8 * 60_000,
  };

  const vote: VoteState = {
    id: "sc-vote",
    action: "kick",
    targetUserId: "u-theo",
    targetName: "theo",
    yes: ["u-noor", "u-ria"],
    no: ["u-leo"],
    createdBy: "u-noor",
    createdByName: "noor",
    createdAt: Date.now() - 15_000,
    expiresAt: Date.now() + 45_000,
    closed: false,
  };

  const users: RoomUser[] = [
    { id: "u-theo", username: "theo", color: "#f5a623", avatarSeed: "sc-theo" },
    { id: "u-noor", username: "noor", color: "#a970ff", avatarSeed: "sc-noor" },
    { id: "u-ria", username: "ria", color: "#ff5d5d", avatarSeed: "sc-ria" },
    { id: "u-maya", username: "maya", color: "#7ee2a8", avatarSeed: "sc-maya" },
    { id: "u-leo", username: "leo", color: "#45b7d1", avatarSeed: "sc-leo" },
    { id: "u-jules", username: "jules", color: "#ff9db1", avatarSeed: "sc-jules" },
    { id: "u-ivy", username: "ivy", color: "#3ddc97", avatarSeed: "sc-ivy" },
    { id: "u-carol", username: "carol", color: "#c9a8ff", avatarSeed: "sc-carol" },
    { id: "u-nico", username: "nico", color: "#ff6b6b", avatarSeed: "sc-nico" },
    { id: "u-ash", username: "ash", color: "#4ecdc4", avatarSeed: "sc-ash" },
    { id: "u-priya", username: "priya", color: "#ffeaa7", avatarSeed: "sc-priya" },
    { id: "u-owen", username: "owen", color: "#85c1e9", avatarSeed: "sc-owen" },
    { id: "u-luna", username: "luna", color: "#dda0dd", avatarSeed: "sc-luna" },
    { id: "u-kai", username: "kai", color: "#82e0aa", avatarSeed: "sc-kai" },
    { id: "u-bea", username: "bea", color: "#f8c471", avatarSeed: "sc-bea" },
    { id: "u-sam", username: "sam", color: "#bb8fce", avatarSeed: "sc-sam" },
    { id: "u-dre", username: "dre", color: "#98d8c8", avatarSeed: "sc-dre" },
    { id: "u-mina", username: "mina", color: "#f7dc6f", avatarSeed: "sc-mina" },
    { id: "u-jo", username: "jo", color: "#ff8fab", avatarSeed: "sc-jo" },
    { id: "u-vale", username: "vale", color: "#6c8cff", avatarSeed: "sc-vale" },
    { id: "u-henrik", username: "henrik", color: "#3ddc97", avatarSeed: "sc-henrik" },
    { id: "u-tessa", username: "tessa", color: "#ffb347", avatarSeed: "sc-tessa" },
    { id: "u-oz", username: "oz", color: "#7fdbda", avatarSeed: "sc-oz" },
    { id: "u-quin", username: "quin", color: "#c084fc", avatarSeed: "sc-quin" },
  ];

  return { messages, poll, vote, users };
}

type ShowcaseLayer = {
  messages: ChatItem[];
  poll: PollState | null;
  vote: VoteState;
  users: RoomUser[];
};

/** Local playground so fixture poll/vote/react/reply work without the server. */
export function useShowcasePlayground(
  enabled: boolean,
  me: { id: string; username: string } | null,
) {
  const [layer, setLayer] = useState<ShowcaseLayer | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLayer(null);
      return;
    }
    setLayer(showcaseFixtures(me?.username ?? "you", me?.id));
  }, [enabled, me?.id, me?.username]);

  const react = useCallback(
    (messageId: string, emoji: string, userId: string): boolean => {
      if (!isShowcaseId(messageId)) return false;
      setLayer((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((item) => {
            if (item.type !== "chat" || item.id !== messageId) return item;
            const chat = item as ChatBroadcastMessage;
            const current = chat.reactions?.[emoji] ?? [];
            const nextIds = current.includes(userId)
              ? current.filter((id) => id !== userId)
              : [...current, userId];
            const reactions = { ...(chat.reactions ?? {}) };
            if (nextIds.length === 0) delete reactions[emoji];
            else reactions[emoji] = nextIds;
            return { ...chat, reactions };
          }),
        };
      });
      return true;
    },
    [],
  );

  const pollVote = useCallback(
    (pollId: string, optionIndex: number, userId: string): boolean => {
      if (!isShowcaseId(pollId)) return false;
      setLayer((prev) => {
        if (!prev?.poll || prev.poll.id !== pollId || prev.poll.closed) {
          return prev;
        }
        const votes = prev.poll.votes.map((ids) =>
          ids.filter((id) => id !== userId),
        );
        const row = votes[optionIndex] ?? [];
        votes[optionIndex] = [...row, userId];
        return { ...prev, poll: { ...prev.poll, votes } };
      });
      return true;
    },
    [],
  );

  const pollClose = useCallback((pollId: string): boolean => {
    if (!isShowcaseId(pollId)) return false;
    setLayer((prev) => {
      if (!prev?.poll || prev.poll.id !== pollId) return prev;
      const announcement: ChatItem = {
        type: "system",
        kind: "poll",
        text: formatPollClosedText(prev.poll),
        timestamp: Date.now(),
      };
      return {
        ...prev,
        poll: null,
        messages: [...prev.messages, announcement],
      };
    });
    return true;
  }, []);

  const voteCast = useCallback(
    (voteId: string, approve: boolean, userId: string): boolean => {
      if (!isShowcaseId(voteId)) return false;
      setLayer((prev) => {
        if (!prev || prev.vote.id !== voteId || prev.vote.closed) return prev;
        const yes = prev.vote.yes.filter((id) => id !== userId);
        const no = prev.vote.no.filter((id) => id !== userId);
        if (approve) yes.push(userId);
        else no.push(userId);
        return { ...prev, vote: { ...prev.vote, yes, no } };
      });
      return true;
    },
    [],
  );

  const replyHint = useCallback((replyToId: string): ChatReplyRef | undefined => {
    if (!isShowcaseId(replyToId) || !layer) return undefined;
    const parent = layer.messages.find(
      (m) => m.type === "chat" && m.id === replyToId,
    );
    if (!parent || parent.type !== "chat") return undefined;
    return {
      id: parent.id,
      username: parent.username,
      text: parent.text.slice(0, 120),
    };
  }, [layer]);

  return { layer, react, pollVote, pollClose, voteCast, replyHint };
}
